import get from 'lodash/get';

import { actions as _act } from '@/models/store.actions';
import store from '@/store/store';
import { IitCloudProviderSettings, IManagingCompanyInfo } from '@/models/settings.models';
import { DocumentActionType, LoginType } from '@/models/enums';
import { isOrganisationUser } from '@/helpers/auth-helper';

class GlobalSettingsHelper {
  get appSettings(): any {
    return store.getters[_act.SETT_GET_APPSET] || {};
  }

  get globalSettings(): any {
    return get(this.appSettings, 'globalSettings.globalSettingItems') || {};
  }

  get isModulesEnabled(): boolean {
    return get(this.globalSettings, 'modulesEnabled.settingValue') === '1';
  }

  get isMonetizationEnabled(): boolean {
    return get(this.globalSettings, 'workmode.settingValue') === '0';
  }

  get isDiscountsEnabled(): boolean {
    return get(this.globalSettings, 'discountsEnabled.settingValue') === '1';
  }

  get isReferralLinkEnabled(): boolean {
    return get(this.globalSettings, 'referralLinkEnabled.settingValue') === '1';
  }

  get frontEndViews(): object | null {
    let val = null;
    let frontEndViews = get(this.globalSettings, 'frontEndViews.settingValue');
    if (frontEndViews) {
      val = JSON.parse(frontEndViews);
    }
    return val;
  }

  get isUnregUserDocumentProcessingEnabled(): boolean {
    return get(this.globalSettings, 'unregUserDocumentProcessing.settingValue') === '1';
  }

  get isInfoPlusSystem(): boolean {
    return get(this.globalSettings, 'aoSystemId.settingValue') === '1' &&
           get(this.globalSettings, 'systemName.settingValue') === 'askod_online';
  }

  get managingCompanyInfo(): IManagingCompanyInfo {
    let managingCompanyInfo = get(this.globalSettings, 'managingCompanyInfo.settingValue');
    return (managingCompanyInfo ? JSON.parse(managingCompanyInfo) : {}) as IManagingCompanyInfo;
  }

  get isRoamingEnabled(): boolean {
    return get(this.globalSettings, 'roamingEnabled.settingValue') === '1';
  }

  get isHideSendBtnOnDocumentAdd(): boolean {
    return get(this.globalSettings, 'hideSendBtnOnDocumentAdd.settingValue') === '1';
  }

  get allowedDocumentActionTypes(): Array<DocumentActionType> {
    let allowedTypes: Array<DocumentActionType> = [];
    let settVal = get(this.globalSettings, 'allowedDocumentActionTypes.settingValue');
    if (settVal) {
      let arr = settVal.split(',');
      for (const arrElement of arr) {
        let type = DocumentActionType[arrElement.trim()];
        if (type && !allowedTypes.includes(type)) allowedTypes.push(type);
      }
    }
    return allowedTypes;
  }

  get isAcquaintActionAllowed(): boolean { return this.allowedDocumentActionTypes.includes(DocumentActionType.Acquaint); }

  get isAgreeActionAllowed(): boolean { return this.allowedDocumentActionTypes.includes(DocumentActionType.Agree); }

  get isSignActionAllowed(): boolean { return this.allowedDocumentActionTypes.includes(DocumentActionType.Sign); }

  get isRegistrationEnabled(): boolean { return ['0', '1'].includes(get(this.globalSettings, 'registrationMode.settingValue')); }

  get isRegistrationByAdminMode(): boolean { return get(this.globalSettings, 'registrationMode.settingValue') === '2'; }

  get isRegistrationKeyRequired(): boolean { return get(this.globalSettings, 'registrationMode.settingValue') === '1'; }

  get defaultLoginType(): string { return get(this.globalSettings, 'loginType.settingValue'); }

  get isIncomingDocumentParamsAllowed(): boolean {
    return get(this.globalSettings, 'allowIncomingDocumentParams.settingValue') === '1' && isOrganisationUser();
  }

  get isMOSystem(): boolean { return get(this.globalSettings, 'systemName.settingValue') === 'askod_online_mo'; }

  get isSurveyEnabled(): boolean { return get(this.globalSettings, 'surveysEnabled.settingValue') === '1'; }
}

export const globalSettingsHelper = new GlobalSettingsHelper();

class AppSettingsHelper {
  get appSettings(): any { return store.getters[_act.SETT_GET_APPSET] || {}; }

  private get _allowedLoginTypes(): Array<string> { return this.appSettings.allowedLoginTypes || []; }

  private _isLoginEnabled(loginType: LoginType): boolean { return this._allowedLoginTypes.includes(loginType); }

  get isLoginByEmailEnabled(): boolean { return this._isLoginEnabled(LoginType.Email); }

  get isLoginByDigitalSignatureEnabled(): boolean { return this._isLoginEnabled(LoginType.DigitalSignature); }

  get isLoginByGoogleEnabled(): boolean { return this._isLoginEnabled(LoginType.Google); }

  get isLoginByFacebookEnabled(): boolean { return this._isLoginEnabled(LoginType.Facebook); }

  get isLoginByMicrosoftEnabled(): boolean { return this._isLoginEnabled(LoginType.Microsoft); }

  get isLoginByAppleEnabled(): boolean { return this._isLoginEnabled(LoginType.Apple); }

  get isCloudKeyEnabled(): boolean { return (this.appSettings['cloudKey'] || {})['enabled'] || false; }

  get isCloudKeySupportingRsaAndEcdsa(): boolean { return (this.appSettings['cloudKey'] || {})['isRsaEcdsaSupported'] || false; }

  get isGoogleDiskDisabled(): boolean { return this.appSettings.googleDiskDisabled || false; }

  get isDepositSignEnabled(): boolean { return (this.appSettings['depositSign'] || {})['enabled'] || false; }

  get isDiiaEnabled(): boolean { return (this.appSettings['diia'] || {})['enabled'] || false; }

  get iitCloudProviderSettings(): IitCloudProviderSettings[] {
    return (this.appSettings.iitCloudProviderSettings || []).map((s: any) => new IitCloudProviderSettings(s));
  }
}

export const appSettingsHelper = new AppSettingsHelper();
